import {
  type DimensionValue,
  Pressable,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
  useWindowDimensions,
} from "react-native";
import Modal from "react-native-modal";

import { isNative, useDebouncedCallback } from "@stepapp/core";

import { useCallback } from "react";
import { useSafeAreaInsets } from "../SafeAreaExports";
import { useBreakpoints } from "../hooks/useBreakpoints";
import { palette, spacing } from "../styles";
import Block from "./Block";

type Props = {
  avoidKeyboard?: boolean;
  closeOnBackdropPress?: boolean;
  children: NotWorth;
  contentStyle?: StyleProp<ViewStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  enableScroll?: boolean;
  handleOnPress?: () => void;
  propagateSwipe?: boolean;
  toggle: () => void;
  visible: boolean;
  id?: string;
};

const BottomSheet = ({
  avoidKeyboard = false,
  children,
  contentStyle,
  containerStyle,
  enableScroll = isNative,
  handleOnPress,
  propagateSwipe,
  toggle,
  closeOnBackdropPress = true,
  visible,
  id,
}: Props) => {
  const insets = useSafeAreaInsets();
  const { mobileOnly, md } = useBreakpoints();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const width = md ? 580 : screenWidth;
  const debounceToggle = useDebouncedCallback(toggle, 300);

  const handleBackdropPress = useCallback(() => {
    if (closeOnBackdropPress) {
      debounceToggle();
    }
  }, [closeOnBackdropPress, debounceToggle]);

  const isDesktopWeb = !mobileOnly;

  const styles = StyleSheet.create({
    container: isDesktopWeb
      ? {
          justifyContent: "center",
          alignItems: "center",
          padding: "10px" as DimensionValue,
          margin: 0,
          width: screenWidth,
        }
      : {
          alignItems: "center",
          justifyContent: "flex-end",
          margin: 0,
          width: screenWidth,
        },
    content: {
      backgroundColor: palette.common.white,
      ...(isDesktopWeb
        ? {
            borderRadius: spacing.radius * 2,
            padding: spacing.unit * 3,
            width,
          }
        : {
            borderTopLeftRadius: spacing.radius * 2,
            borderTopRightRadius: spacing.radius * 2,
            width: screenWidth,
          }),
    },
    backdrop: {
      flex: 1,
      width: screenWidth,
      minHeight: screenHeight,
      backgroundColor: palette.common.black,
    },
  });

  const Wrapper = handleOnPress ? Pressable : View;

  return (
    <Modal
      hideModalContentWhileAnimating
      useNativeDriverForBackdrop
      animationOutTiming={500}
      avoidKeyboard={avoidKeyboard}
      backdropOpacity={0.5}
      customBackdrop={<Pressable style={styles.backdrop} onPress={handleBackdropPress} />}
      isVisible={visible}
      onBackButtonPress={debounceToggle}
      onBackdropPress={debounceToggle}
      onSwipeComplete={debounceToggle}
      propagateSwipe={propagateSwipe}
      style={[styles.container, containerStyle]}
      useNativeDriver={false}
      id={id}
      {...(enableScroll && { swipeDirection: "down" })}
    >
      <Wrapper onPress={handleOnPress}>
        <Block
          style={[
            styles.content,
            { paddingBottom: insets?.bottom || spacing.unit * 2 },
            contentStyle,
          ]}
        >
          {children}
        </Block>
      </Wrapper>
    </Modal>
  );
};

export default BottomSheet;
